/*
  VSB before and after (sections/vsb-ab-player). Each clip has two <audio
  preload="none"> elements, the before and the after. Play starts both in step
  with the one not selected muted, so the switch flips between them instantly
  and without a gap, keeping the place. One clip on the page at a time: the
  'd:play' event stops the others, and the audio previews and videos too.
  A clip with one file only (a product's older audio previews) plays it
  plainly: no switch, no second file.
  Set up once the section nears the screen (vsbWhenNear), or on first focus.
*/

if (!customElements.get('d-ab')) {
  customElements.define('d-ab', class extends HTMLElement {
    connectedCallback() {
      this.init = this.init.bind(this);
      this.addEventListener('focusin', this.init, { once: true });
      if (window.vsbWhenNear) window.vsbWhenNear(this, this.init); else this.init();
    }

    init() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.clips = [...this.querySelectorAll('.d-ab__clip')];
      this.clips.forEach((clip) => {
        const [before, after] = ['before', 'after'].map((s) => clip.querySelector(`audio[data-side="${s}"]`));
        clip.audio = { before, after };
        this.applySide(clip);
        clip.querySelector('.d-ab__play').addEventListener('click', () => (this.current === clip && !before.paused ? this.pause() : this.play(clip)));
        clip.querySelector('.d-ab__toggle')?.addEventListener('click', () => this.flip(clip));
        clip.querySelectorAll('.d-ab__side').forEach((s) => s.addEventListener('click', () => { if (clip.dataset.side !== s.dataset.for) this.flip(clip); }));
        before.addEventListener('timeupdate', () => {
          if (before.duration) clip.style.setProperty('--progress', before.currentTime / before.duration);
        });
        before.addEventListener('ended', () => { after?.pause(); this.setPlaying(clip, false); clip.style.setProperty('--progress', 0); });
      });
      document.addEventListener('d:play', (e) => { if (e.detail !== this) this.pause(); });
    }

    applySide(clip) {
      if (!clip.audio.after) return;
      const on = clip.dataset.side === 'after';
      clip.audio.before.muted = on;
      clip.audio.after.muted = !on;
      clip.querySelector('.d-ab__toggle').setAttribute('aria-checked', String(on));
    }

    flip(clip) {
      if (!clip.audio.after) return;
      clip.dataset.side = clip.dataset.side === 'after' ? 'before' : 'after';
      const { before, after } = clip.audio;
      if (!before.paused && Math.abs(after.currentTime - before.currentTime) > 0.05) after.currentTime = before.currentTime;
      this.applySide(clip);
    }

    play(clip) {
      if (this.current && this.current !== clip) this.stop(this.current);
      this.current = clip;
      document.dispatchEvent(new CustomEvent('d:play', { detail: this }));
      const { before, after } = clip.audio;
      if (after) after.currentTime = before.currentTime || 0;
      this.applySide(clip);
      Promise.all([before.play(), after?.play()])
        .then(() => this.setPlaying(clip, true))
        .catch((e) => { this.lastError = e; before.pause(); after?.pause(); this.setPlaying(clip, false); });
      this.setPlaying(clip, true);
    }

    stop(clip) {
      clip.audio.before.pause();
      clip.audio.after?.pause();
      this.setPlaying(clip, false);
    }

    pause() { if (this.current) this.stop(this.current); }

    setPlaying(clip, on) {
      clip.classList.toggle('is-playing', on);
      const b = clip.querySelector('.d-ab__play');
      // A toggle button: aria-pressed says playing or not; its name (the
      // section's "Play" setting and the clip's title) stays as it is.
      b.setAttribute('aria-pressed', String(on));
    }
  });
}
